import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Button, Dropdown, Layout, Menu, message, Popover, Select, Spin, Tag, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  ApiOutlined, ApartmentOutlined, BookOutlined, CameraOutlined, DashboardOutlined,
  EditOutlined, FileSearchOutlined, FileTextOutlined, FormOutlined, FundOutlined,
  LinkOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, ProfileOutlined,
  RobotOutlined, SearchOutlined, SettingOutlined, SolutionOutlined, StarOutlined,
  UserOutlined, WarningOutlined,
} from '@ant-design/icons';

import Login from './pages/Login';
import { authApi, authStore, coursewareApi, type AuthUser, type Courseware } from './services/api';
import { selectionApi } from './services/selection-api';
import { teachingContextStore, useTeachingContext, type TeachingContext } from './services/teaching-context';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const QuestionBank = lazy(() => import('./pages/QuestionBank'));
const QuestionEdit = lazy(() => import('./pages/QuestionBank/Edit'));
const QuestionImport = lazy(() => import('./pages/QuestionBank/Import'));
const KnowledgeTree = lazy(() => import('./pages/KnowledgeTree'));
const Selection = lazy(() => import('./pages/Selection'));
const ComposeWorkspace = lazy(() => import('./pages/ComposeWorkspace'));
const Papers = lazy(() => import('./pages/Papers'));
const PaperDetail = lazy(() => import('./pages/Papers/Detail'));
const Courseware = lazy(() => import('./pages/Courseware'));
const AIGenerate = lazy(() => import('./pages/AIGenerate'));
const LessonPlans = lazy(() => import('./pages/LessonPlans'));
const ReviewPpt = lazy(() => import('./pages/ReviewPpt'));
const ScanCompose = lazy(() => import('./pages/ScanCompose'));
const ScanGrade = lazy(() => import('./pages/ScanGrade'));
const Settings = lazy(() => import('./pages/Settings'));
const SearchPage = lazy(() => import('./pages/Search'));
const Collections = lazy(() => import('./pages/Collections'));
const OctopIntegration = lazy(() => import('./pages/OctopIntegration'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Mistakes = lazy(() => import('./pages/Mistakes'));
const WorkJobs = lazy(() => import('./pages/WorkJobs'));
const WeixinAssistant = lazy(() => import('./pages/WeixinAssistant'));

const { Header, Sider, Content } = Layout;
type MenuItem = Required<MenuProps>['items'][number];

const menuItems: MenuItem[] = [
  { key: 'workspace', icon: <DashboardOutlined />, label: '工作台', children: [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '教学概况' },
    { key: '/select/compose', icon: <FormOutlined />, label: '智能组卷' },
    { key: '/select/knowledge', icon: <ApartmentOutlined />, label: '知识点选题' },
    { key: '/select/chapter', icon: <BookOutlined />, label: '章节选题' },
  ] },
  { key: 'resources', icon: <BookOutlined />, label: '教学资源', children: [
    { key: '/search', icon: <SearchOutlined />, label: '搜题' },
    { key: '/questions/manage', icon: <FileTextOutlined />, label: '题库管理' },
    { key: '/papers', icon: <ProfileOutlined />, label: '试卷与发布' },
    { key: '/collections', icon: <StarOutlined />, label: '我的收藏' },
    { key: '/knowledge', icon: <ApartmentOutlined />, label: '知识点' },
    { key: '/courseware', icon: <BookOutlined />, label: '课件' },
  ] },
  { key: 'creation', icon: <EditOutlined />, label: '教学创作', children: [
    { key: '/ai-generate', icon: <RobotOutlined />, label: 'AI 出题' },
    { key: '/lesson-plans', icon: <SolutionOutlined />, label: 'AI 教案' },
    { key: '/review-ppt', icon: <EditOutlined />, label: '讲评 PPT' },
    { key: '/scan-compose', icon: <CameraOutlined />, label: '拍照组卷' },
  ] },
  { key: 'evaluation', icon: <FundOutlined />, label: '评测改进', children: [
    { key: '/scan-grade', icon: <FileSearchOutlined />, label: '拍照批改' },
    { key: '/analytics', icon: <FundOutlined />, label: '学情分析' },
    { key: '/mistakes', icon: <WarningOutlined />, label: '错题本' },
  ] },
  { key: 'integration', icon: <SettingOutlined />, label: '集成与设置', children: [
    { key: '/weixin', icon: <ApiOutlined />, label: '微信助手' },
    { key: '/octop', icon: <ApiOutlined />, label: "腾岳Ai 智能体" },
    { key: '/settings', icon: <SettingOutlined />, label: '系统设置' },
    { key: '/jobs', icon: <ProfileOutlined />, label: '后台任务' },
  ] },
];

const NAV_GROUP_KEYS = ['workspace', 'resources', 'creation', 'evaluation', 'integration'];
const NAV_STORAGE_KEY = 'tengyue:sidebar:v1';
const readNavigationPreferences = (): { collapsed: boolean; openGroups: string[] } => {
  try {
    const raw = JSON.parse(localStorage.getItem(NAV_STORAGE_KEY) || 'null');
    return { collapsed: raw?.collapsed === true,
      openGroups: Array.isArray(raw?.openGroups) ? raw.openGroups.filter((key: unknown) => typeof key === 'string' && NAV_GROUP_KEYS.includes(key)) : [...NAV_GROUP_KEYS] };
  } catch { return { collapsed: false, openGroups: [...NAV_GROUP_KEYS] }; }
};

const pageContext: Array<{ path: string; title: string; description: string }> = [
  {path:'/jobs',title:'后台任务',description:'查看进度、恢复处理与打开结果'},
  {path:'/weixin',title:'微信助手',description:'微信扫码绑定、组卷与批改'},
  {path:'/select/chapter',title:'章节选题',description:'按教材章节或教学主题筛选试题'},
  {path:'/select/knowledge',title:'知识点选题',description:'按知识点范围挑选试题'},
  {path:'/select/compose',title:'智能组卷',description:'检查题量与缺口，生成可调整试卷'},
  {path:'/questions/manage',title:'题库管理',description:'维护题目与审校状态'},
  { path: '/questions', title: '题库', description: '管理、维护和复用教学题目' },
  { path: '/papers', title: '试卷与发布', description: '查看试卷、完善内容并组织发布' },
  { path: '/compose', title: '智能组卷', description: '按教学目标快速生成可编辑试卷' },
  { path: '/search', title: '搜题', description: '从题库中定位合适的题目' },
  { path: '/courseware', title: '课件', description: '沉淀教材与课堂资源' },
  { path: '/ai-generate', title: 'AI 出题', description: '结合知识点生成教学题目' },
  { path: '/lesson-plans', title: 'AI 教案', description: '生成并维护课堂教学设计' },
  { path: '/review-ppt', title: '讲评 PPT', description: '将评测结果转化为讲评材料' },
  { path: '/scan-compose', title: '拍照组卷', description: '从纸质试卷导入教学资源' },
  { path: '/scan-grade', title: '拍照批改', description: '识别答题卡并记录客观题成绩' },
  { path: '/analytics', title: '学情分析', description: '追踪班级与学生的学习表现' },
  { path: '/mistakes', title: '错题本', description: '聚焦高频错误并安排巩固练习' },
  { path: '/knowledge', title: '知识点', description: '建立教材与题目的知识结构' },
  { path: '/collections', title: '我的收藏', description: '整理常用题目与教学资源' },
  { path: '/octop', title: "腾岳Ai 智能体", description: '管理智能体、通道和技能连接' },
  { path: '/settings', title: '系统设置', description: '维护账户与平台配置' },
  { path: '/dashboard', title: '教学概况', description: '从当前资源与任务开始今天的备课' },
];

const userMenuItems: MenuProps['items'] = [
  { key: 'profile', icon: <UserOutlined />, label: '个人设置' },
  { type: 'divider' },
  { key: 'logout', icon: <LogoutOutlined />, danger: true, label: '退出登录' },
];
const ROLE_LABEL: Record<string, string> = { admin: '管理员', teacher: '教师', student: '学生' };

const PageLoader: React.FC = () => <div style={{ minHeight: 280, display: 'grid', placeItems: 'center' }} aria-live="polite"><Spin tip="正在加载工作区…" /></div>;

const App: React.FC = () => {
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => readNavigationPreferences().collapsed);
  const [openGroups, setOpenGroups] = useState<string[]>(() => readNavigationPreferences().openGroups);
  const [popupGroups, setPopupGroups] = useState<string[]>([]);
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 991px)').matches);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const collapsed = narrow ? !mobileExpanded : desktopCollapsed;
  const toggleNavigation = () => {
    setPopupGroups([]);
    if (narrow) setMobileExpanded(value => !value);
    else setDesktopCollapsed(value => !value);
  };
  useEffect(() => {
    try { localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify({collapsed: desktopCollapsed, openGroups})); }
    catch { /* Navigation remains usable when browser storage is unavailable. */ }
  }, [desktopCollapsed, openGroups]);
  const navigate = useNavigate();
  const location = useLocation();
  const onLoginPage = location.pathname === '/login';
  const [checking, setChecking] = useState(true);
  const [authRequired, setAuthRequired] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(authStore.getUser());
  const teachingContext = useTeachingContext();
  const [coursewares, setCoursewares] = useState<Courseware[]>([]);
  const [catalogBooks,setCatalogBooks]=useState<any[]>([]);
  const [coursewaresLoaded, setCoursewaresLoaded] = useState(false);

  const refreshCoursewares = () => coursewareApi.getList({ page: 1, pageSize: 100 }).then((response: any) => {
    setCoursewares(response?.data?.list || []);
    setCoursewaresLoaded(true);
  }).catch(() => setCoursewaresLoaded(true));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg: any = await authApi.getConfig();
        const required = cfg?.data?.authRequired !== false;
        if (cancelled) return;
        setAuthRequired(required);
        if (!required || !authStore.getToken()) return;
        const me: any = await authApi.getMe();
        if (me?.data && !cancelled) { authStore.updateUser(me.data); setUser(me.data); }
      } catch {
        // 401 由请求拦截器清理会话并跳转登录页。
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onLoginPage]);

  useEffect(() => {
    if (onLoginPage) return;
    let cancelled = false;
    coursewareApi.getList({ page: 1, pageSize: 100 }).then((response: any) => {
      if (!cancelled) {
        setCoursewares(response?.data?.list || []);
        setCoursewaresLoaded(true);
      }
    }).catch(() => { if (!cancelled) setCoursewaresLoaded(true); });
    selectionApi.books().then((response:any)=>{if(!cancelled)setCatalogBooks(response.data||[]);}).catch(()=>{});
    const onCoursewaresChanged = () => { void refreshCoursewares(); };
    window.addEventListener('edupaper:coursewares-changed', onCoursewaresChanged);
    if (!authStore.getToken()) return () => {
      cancelled = true;
      window.removeEventListener('edupaper:coursewares-changed', onCoursewaresChanged);
    };
    authApi.getSettings().then((response: any) => {
      if (cancelled) return;
      const values = response?.data?.effective || response?.data?.settings;
      if (!values) return;
      const next: TeachingContext = {
        subject: values.defaultSubject || values.default_subject || '数学',
        grade: values.defaultGrade || values.default_grade || '高一',
        textbookVersion: values.defaultTextbookVersion || values.default_textbook_version || '人教版',
        coursewareId: values.defaultCoursewareId || values.default_courseware_id || undefined,
      };
      teachingContextStore.set(next);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
      window.removeEventListener('edupaper:coursewares-changed', onCoursewaresChanged);
    };
  }, [onLoginPage]);

  const scopes = useMemo(() => [...catalogBooks.map(book=>({subject:book.subject,grade:book.grade,textbookVersion:book.textbookVersion})),...coursewares
    .filter((item) => item.subject?.trim() && item.textbookVersion?.trim())
    .flatMap((item) => (item.grade ? [item.grade] : item.provenance?.applicableGrades || []).map((grade: string) => ({
      subject: item.subject.trim(),
      grade: grade.trim(),
      textbookVersion: item.textbookVersion!.trim(),
    })))], [coursewares,catalogBooks]);
  const sortOptions = (values: string[]) => [...new Set(values)].sort((a, b) => a.localeCompare(b, 'zh-CN', { numeric: true }));
  const gradeOrder = ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级', '初一', '初二', '初三', '高一', '高二', '高三'];
  const availableSubjects = useMemo(() => sortOptions(scopes.map((scope) => scope.subject)), [scopes]);
  const selectedSubject = availableSubjects.includes(teachingContext.subject) ? teachingContext.subject : availableSubjects[0];
  const availableGrades = useMemo(() => sortOptions(scopes.filter((scope) => scope.subject === selectedSubject).map((scope) => scope.grade)).sort((a,b) => (gradeOrder.includes(a) ? gradeOrder.indexOf(a) : 99) - (gradeOrder.includes(b) ? gradeOrder.indexOf(b) : 99)), [scopes, selectedSubject]);
  const selectedGrade = availableGrades.includes(teachingContext.grade) ? teachingContext.grade : availableGrades[0];
  const availableVersions = useMemo(() => sortOptions(scopes
    .filter((scope) => scope.subject === selectedSubject && scope.grade === selectedGrade)
    .map((scope) => scope.textbookVersion)), [scopes, selectedSubject, selectedGrade]);
  const selectedVersion = availableVersions.includes(teachingContext.textbookVersion) ? teachingContext.textbookVersion : availableVersions[0];

  useEffect(() => {
    if (!coursewaresLoaded || !selectedSubject || !selectedGrade || !selectedVersion) return;
    if (selectedSubject !== teachingContext.subject || selectedGrade !== teachingContext.grade || selectedVersion !== teachingContext.textbookVersion) {
      void saveTeachingContext({ subject: selectedSubject, grade: selectedGrade, textbookVersion: selectedVersion });
    }
  }, [coursewaresLoaded, selectedSubject, selectedGrade, selectedVersion, teachingContext.subject, teachingContext.grade, teachingContext.textbookVersion]);

  const currentPage = useMemo(() => {
    const path = location.pathname === '/' ? '/dashboard' : location.pathname;
    return [...pageContext].sort((a, b) => b.path.length - a.path.length)
      .find((item) => path === item.path || path.startsWith(`${item.path}/`)) ?? pageContext[pageContext.length - 1];
  }, [location.pathname]);
  const selectedMenuKey = currentPage.path === '/questions' ? '/questions/manage' : currentPage.path;
  const previousMenuKey = useRef(selectedMenuKey);
  useEffect(() => {
    if (onLoginPage || previousMenuKey.current === selectedMenuKey) return;
    previousMenuKey.current = selectedMenuKey;
    const parent = menuItems.find(item => item && 'children' in item && item.children?.some(child => child?.key === selectedMenuKey));
    if (parent?.key) setOpenGroups(keys => keys.includes(String(parent.key)) ? keys : [...keys, String(parent.key)]);
    setPopupGroups([]);
    setMobileExpanded(false);
  }, [selectedMenuKey, onLoginPage]);
  useEffect(() => {
    if (!narrow || !mobileExpanded) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMobileExpanded(false); setPopupGroups([]); }
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [narrow, mobileExpanded]);
  const octopDashboardUrl = (import.meta as any).env?.VITE_OCTOP_DASHBOARD_URL as string | undefined;
  const openOctop = () => window.open(octopDashboardUrl || `${window.location.protocol}//${window.location.hostname}:8088`, '_blank', 'noopener,noreferrer');

  const saveTeachingContext = async (patch: Partial<TeachingContext>) => {
    const scopeChanged = ['subject', 'grade', 'textbookVersion'].some((key) => key in patch && patch[key as keyof TeachingContext] !== teachingContext[key as keyof TeachingContext]);
    const next = { ...teachingContext, ...patch, ...(scopeChanged ? { coursewareId: undefined, coursewareTitle: undefined } : {}) };
    teachingContextStore.set(next);
    try {
      if (authStore.getToken()) {
        await authApi.updateSettings({
          default_subject: next.subject,
          default_grade: next.grade,
          default_textbook_version: next.textbookVersion,
          default_courseware_id: next.coursewareId ? Number(next.coursewareId) : null,
        });
      }
    } catch {
      message.warning('当前教学范围已在本机生效，但未能同步到账号');
    }
  };

  const teachingContextPanel = (
    <div style={{ width: 288 }}>
      <strong>当前教学范围</strong>
      <p style={{ margin: '4px 0 12px', color: '#64748b', fontSize: 13 }}>新建课件、组卷、出题和教案默认使用这里的设置。</p>
      <label style={{ display: 'block', marginBottom: 6 }}>学科</label>
      <Select value={selectedSubject} style={{ width: '100%', marginBottom: 12 }} showSearch optionFilterProp="label" placeholder="选择学科" options={availableSubjects.map((value) => ({ value, label: value }))} onChange={(subject) => {
        const grade = sortOptions(scopes.filter((scope) => scope.subject === subject).map((scope) => scope.grade))[0];
        const textbookVersion = sortOptions(scopes.filter((scope) => scope.subject === subject && scope.grade === grade).map((scope) => scope.textbookVersion))[0];
        void saveTeachingContext({ subject, grade, textbookVersion });
      }} />
      <label style={{ display: 'block', marginBottom: 6 }}>年级</label>
      <Select value={selectedGrade} style={{ width: '100%', marginBottom: 12 }} showSearch optionFilterProp="label" placeholder="该学科暂无课件" options={availableGrades.map((value) => ({ value, label: value }))} onChange={(grade) => {
        const textbookVersion = sortOptions(scopes.filter((scope) => scope.subject === selectedSubject && scope.grade === grade).map((scope) => scope.textbookVersion))[0];
        void saveTeachingContext({ grade, textbookVersion });
      }} />
      <label style={{ display: 'block', marginBottom: 6 }}>教材版本</label>
      <Select value={selectedVersion} style={{ width: '100%' }} showSearch optionFilterProp="label" placeholder="该年级暂无课件" options={availableVersions.map((value) => ({ value, label: value }))} onChange={(textbookVersion) => void saveTeachingContext({ textbookVersion })} />
      <label style={{ display: 'block', margin: '12px 0 6px' }}>补充课件（可选）</label>
      <Select
        value={teachingContext.coursewareId}
        style={{ width: '100%' }}
        showSearch
        allowClear
        optionFilterProp="label"
        placeholder="使用独立教学目录，无需上传课件"
        options={coursewares
          .filter((item) => item.subject === selectedSubject && (item.grade === selectedGrade || item.provenance?.applicableGrades?.includes(selectedGrade)) && item.textbookVersion === selectedVersion && item.status !== 'deleted')
          .map((item) => ({ value: item.id, label: `${item.subject || '未分类'} · ${item.grade || `${item.stage || '学段'}参考`} · ${item.title}` }))}
        onChange={(coursewareId) => {
          const source = coursewares.find((item) => String(item.id) === String(coursewareId));
          void saveTeachingContext({ coursewareId, coursewareTitle: source?.title });
        }}
      />
    </div>
  );

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      authStore.clear(); setUser(null); message.success('已退出登录'); navigate('/login', { replace: true });
    } else if (key === 'profile') navigate('/settings');
  };

  if (location.pathname === '/login') return <Login />;
  if (checking) return <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: '#f6f8fc' }}><Spin size="large" tip="正在校验登录状态…" /></div>;
  if (authRequired && !authStore.getToken()) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  const displayName = user?.displayName || user?.display_name || user?.username || '未登录';
  return (
    <Layout className="app-shell" data-nav-collapsed={collapsed}>
      <a className="skip-link" href="#main-content">跳到主内容</a>
      <Sider className="app-sider" theme="light" trigger={null} collapsible collapsed={collapsed} width={248} collapsedWidth={72} breakpoint="lg" onBreakpoint={broken => {setNarrow(broken); setMobileExpanded(false); setPopupGroups([]);}}>
        <div className="brand-lockup" aria-label="腾岳Edu AI 教师工作台"><span className="brand-mark"><BookOutlined /></span>{!collapsed && <div className="brand-copy"><div className="brand-name">腾岳Edu AI</div><div className="brand-subtitle">TEACHING WORKSPACE</div></div>}</div>
        <nav id="primary-navigation" className="sider-navigation" aria-label="主导航">
          <Menu className="app-nav" mode="inline" selectedKeys={[selectedMenuKey]} items={menuItems}
            inlineCollapsed={collapsed} openKeys={collapsed ? popupGroups : openGroups} triggerSubMenuAction="click"
            onOpenChange={keys => collapsed ? setPopupGroups(keys) : setOpenGroups(keys)}
            onClick={({ key }) => { navigate(key); setPopupGroups([]); if (narrow) setMobileExpanded(false); }} />
        </nav>
        <div className="sider-footer">
          <Tooltip title={collapsed ? '展开菜单栏' : undefined} placement="right"><Button block className="sider-collapse-button" aria-label={collapsed ? '展开菜单栏' : '收起菜单栏'} aria-controls="primary-navigation" aria-expanded={!collapsed} icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={toggleNavigation}>{!collapsed && '收起菜单栏'}</Button></Tooltip>
          {(!octopDashboardUrl || user?.role === 'admin') && <Tooltip title={octopDashboardUrl ? "打开腾岳Ai管理端" : collapsed ? "进入 腾岳Ai" : undefined} placement="right"><Button block icon={<LinkOutlined />} onClick={openOctop}>{!collapsed && (octopDashboardUrl ? "腾岳Ai管理" : "进入 腾岳Ai")}</Button></Tooltip>}</div>
      </Sider>
      {narrow && !collapsed && <button type="button" className="nav-backdrop" aria-label="关闭导航遮罩" onClick={() => {setMobileExpanded(false); setPopupGroups([]);}} />}
      <Layout className="workspace-layout">
        <Header className="app-header">
          <div className="header-context">
            <Button type="text" className="nav-toggle" aria-label={collapsed ? '展开导航' : '收起导航'} icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} aria-expanded={!collapsed} aria-controls="primary-navigation" onClick={toggleNavigation} />
            <div className="page-context" aria-live="polite"><strong>{currentPage.title}</strong><span>{currentPage.description}</span></div>
          </div>
          <div className="header-actions">
            <Popover content={teachingContextPanel} title={null} trigger="click" placement="bottomRight">
              <Button type="text" className="teaching-scope-trigger" aria-label="设置当前教学范围">{teachingContext.subject} · {teachingContext.grade} · {teachingContext.textbookVersion}{teachingContext.coursewareTitle || coursewares.find((item) => String(item.id) === String(teachingContext.coursewareId))?.title ? ` · ${teachingContext.coursewareTitle || coursewares.find((item) => String(item.id) === String(teachingContext.coursewareId))?.title}` : ''}</Button>
            </Popover>
            <Button className="primary-workflow" type="primary" icon={<FormOutlined />} onClick={() => navigate('/compose')}>智能组卷</Button>
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight" trigger={['click']}>
              <Button type="text" className="user-trigger" aria-label="打开个人菜单"><Avatar size={32} icon={<UserOutlined />} style={{ background: '#2563eb' }}>{displayName.slice(0, 1).toUpperCase()}</Avatar><span className="user-name">{displayName}</span>{user?.role && <Tag color={user.role === 'admin' ? 'gold' : 'blue'} style={{ marginInlineEnd: 0 }}>{ROLE_LABEL[user.role] || user.role}</Tag>}</Button>
            </Dropdown>
          </div>
        </Header>
        <Content id="main-content" className="app-main" tabIndex={-1}><div className="content-surface"><Suspense fallback={<PageLoader />}><Routes>
          <Route path="/" element={<Dashboard />} /><Route path="/dashboard" element={<Dashboard />} /><Route path="/construction" element={<Navigate to="/questions/manage?tab=coverage" replace />} /><Route path="/jobs" element={<WorkJobs />} /><Route path="/weixin" element={<WeixinAssistant />} />
          <Route path="/search" element={<SearchPage />} /><Route path="/questions" element={<Navigate to="/select/chapter" replace />} /><Route path="/questions/manage" element={<QuestionBank />} /><Route path="/select/:mode" element={<Selection />} /><Route path="/collections" element={<Collections />} /><Route path="/octop" element={<OctopIntegration />} />
          <Route path="/questions/edit/:id" element={<QuestionEdit />} /><Route path="/questions/edit" element={<QuestionEdit />} /><Route path="/questions/import" element={<QuestionImport />} />
          <Route path="/knowledge" element={<KnowledgeTree />} /><Route path="/compose" element={<Navigate to="/select/compose" replace />} /><Route path="/compose/advanced" element={<Navigate to="/select/compose/prompt" replace />} /><Route path="/select/compose/:method?" element={<ComposeWorkspace />} /><Route path="/papers" element={<Papers />} /><Route path="/papers/:id" element={<PaperDetail />} />
          <Route path="/courseware" element={<Courseware />} /><Route path="/ai-generate" element={<AIGenerate />} /><Route path="/lesson-plans" element={<LessonPlans />} /><Route path="/review-ppt" element={<ReviewPpt />} />
          <Route path="/scan-compose" element={<ScanCompose />} /><Route path="/scan-grade" element={<ScanGrade />} /><Route path="/analytics" element={<Analytics />} /><Route path="/mistakes" element={<Mistakes />} /><Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes></Suspense></div></Content>
      </Layout>
    </Layout>
  );
};

export default App;
